import React, { useRef, useEffect } from 'react';
import { ChatMessage as ChatMessageType, ChatAction } from '../../types/chat';
import { LEAD_TABLE_STATUS } from '../../types';

interface TableContext {
  status?: string;
  type?: 'companies' | 'people' | 'custom';
  [key: string]: any;
}

interface UseChatCompletionProps {
  tableId?: string;
  tableContext?: TableContext;
  leadCount: number;
  setMessages: React.Dispatch<React.SetStateAction<ChatMessageType[]>>;
}

/**
 * Hook for adding completion/no-results messages when dataset status changes
 * Handles transitions from GENERATING to final states
 */
export function useChatCompletion({
  tableId,
  tableContext,
  leadCount,
  setMessages,
}: UseChatCompletionProps): void {
  const addedCompletionMessageRef = useRef<string | null>(null);
  const prevStatusRef = useRef<string | null>(null);
  const lastCompletionLeadCountRef = useRef<number | null>(null);

  useEffect(() => {
    const currentStatus = tableContext?.status;
    const prevStatus = prevStatusRef.current;

    // Update previous status tracking
    const statusChanged = prevStatus !== currentStatus;
    if (statusChanged) {
      prevStatusRef.current = currentStatus || null;
    }

    // Determine if we should add a completion message
    // Case 1: Transitioning from GENERATING to a final state
    // Case 2: Loading directly into COMPLETED state with leads (and we haven't shown completion yet)
    const isTransitionFromGenerating = prevStatus === LEAD_TABLE_STATUS.GENERATING && currentStatus !== LEAD_TABLE_STATUS.GENERATING;
    const isDirectLoadCompleted = currentStatus === LEAD_TABLE_STATUS.COMPLETED && prevStatus === null && leadCount > 0;

    if (isTransitionFromGenerating || isDirectLoadCompleted) {
      // Prevent duplicate completion messages for this tableId+status+leadCount
      const completionKey = `${tableId}-${currentStatus}-${leadCount}`;
      if (addedCompletionMessageRef.current === completionKey) {
        return;
      }

      // Also check if leadCount is the same as last time (prevents re-adding on re-renders)
      if (currentStatus === LEAD_TABLE_STATUS.COMPLETED && lastCompletionLeadCountRef.current === leadCount) {
        return;
      }

      const timestamp = new Date();
      const messageId = `completion-${tableId}-${Date.now()}`;

      if (currentStatus === LEAD_TABLE_STATUS.COMPLETED && leadCount > 0) {
        addedCompletionMessageRef.current = completionKey;
        lastCompletionLeadCountRef.current = leadCount;

        // Success - found results with emphatic message and action suggestions
        const entityType = tableContext?.type === 'people' ? 'people' : tableContext?.type === 'custom' ? 'results' : 'companies';

        const completionActions: ChatAction[] = [
          { type: 'find_more', description: `Find more ${entityType}`, parameters: {} },
          { type: 'enrich', description: 'Enrich with emails & phones', parameters: {} },
          { type: 'monitor', description: 'Set up weekly monitoring', parameters: {} },
          { type: 'campaign', description: 'Start outbound campaign', parameters: {} },
        ];

        setMessages(prev => {
          // Check if we already have a completion-type message for this specific table
          // Use trailing delimiter to ensure exact table ID match (prevents 'table-1' matching 'table-10')
          const hasCompletionMessage = prev.some(m =>
            m.id.startsWith(`completion-${tableId}-`) || m.id.startsWith(`no-results-${tableId}-`)
          );
          if (hasCompletionMessage) return prev;
          return [...prev, {
            id: messageId,
            role: 'assistant',
            content: `Great news! I found ${leadCount} ${entityType} matching your criteria.\n\nWhat would you like me to do next?`,
            timestamp,
            actions: completionActions
          }];
        });
      } else if (currentStatus === LEAD_TABLE_STATUS.COMPLETED_NO_RESULTS) {
        addedCompletionMessageRef.current = completionKey;

        // No results found - message only, editable criteria shown separately below
        setMessages(prev => {
          // Check if we already have a completion-type message for this specific table
          // Use trailing delimiter to ensure exact table ID match (prevents 'table-1' matching 'table-10')
          const hasCompletionMessage = prev.some(m =>
            m.id.startsWith(`completion-${tableId}-`) || m.id.startsWith(`no-results-${tableId}-`)
          );
          if (hasCompletionMessage) return prev;
          return [...prev, {
            id: `no-results-${tableId}-${Date.now()}`,
            role: 'assistant',
            content: `Unfortunately, I couldn't find any results matching your search criteria. You can edit the criteria below and try again, or start a new search.`,
            timestamp,
          }];
        });
      }
    }
  }, [tableContext?.status, tableContext?.type, leadCount, tableId, setMessages]);
}
